import { useCallback, useState } from 'react';

/**
 * Portable multi-goniometer position state (no component-specific dependencies by design —
 * built for VisioMeter2/VGon but intended for reuse by any measurement overlay, e.g. a
 * future posture-analysis annotator).
 *
 * Owns: an array of goniometer instances (pivot + two arms, all in 0..1 normalized
 * coordinates), which handle is currently grabbed, and the position math (drag, keyboard
 * nudge, horizontal flip). Deliberately does NOT own the DOM: the consumer converts
 * pointer events to normalized points and renders the instances however it likes.
 *
 * Instance order is meaningful to the consumer (e.g. index 0 = client's left leg,
 * index 1 = right for dual-goniometer tests) — this hook never reorders.
 */
export type GoniometerPoint = { x: number; y: number };

export interface GoniometerInstance {
    pivot: GoniometerPoint;
    arm1: GoniometerPoint;
    arm2: GoniometerPoint;
}

export type GoniometerHandleId = 'pivot' | 'arm1' | 'arm2';

export interface ActiveGoniometerHandle {
    instance: number;
    handle: GoniometerHandleId;
}

const HANDLE_IDS: readonly GoniometerHandleId[] = ['pivot', 'arm1', 'arm2'];

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

const moveHandle = (
    instance: GoniometerInstance,
    handle: GoniometerHandleId,
    p: GoniometerPoint
): GoniometerInstance => {
    if (handle === 'pivot') {
        const dx = p.x - instance.pivot.x;
        const dy = p.y - instance.pivot.y;
        return {
            pivot: p,
            arm1: { x: instance.arm1.x + dx, y: instance.arm1.y + dy },
            arm2: { x: instance.arm2.x + dx, y: instance.arm2.y + dy },
        };
    }
    return { ...instance, [handle]: p } as GoniometerInstance;
};

export function useGoniometers(initial?: GoniometerInstance[]) {
    const [instances, setInstances] = useState<GoniometerInstance[]>(
        initial ?? [{ pivot: { x: 0.5, y: 0.5 }, arm1: { x: 0.5, y: 0.3 }, arm2: { x: 0.7, y: 0.5 } }]
    );
    const [active, setActive] = useState<ActiveGoniometerHandle | null>(null);

    const reset = useCallback((next: GoniometerInstance[]) => {
        setInstances(next);
        setActive(null);
    }, []);

    // Hit-test every handle of every instance; the nearest one within the grab radius wins,
    // so overlapping goniometers stay individually draggable.
    const grabAt = useCallback(
        (p: GoniometerPoint, radius: number): ActiveGoniometerHandle | null => {
            let best: ActiveGoniometerHandle | null = null;
            let bestDist = radius;
            instances.forEach((instance, i) => {
                for (const handle of HANDLE_IDS) {
                    const d = Math.hypot(p.x - instance[handle].x, p.y - instance[handle].y);
                    if (d < bestDist) {
                        bestDist = d;
                        best = { instance: i, handle };
                    }
                }
            });
            setActive(best);
            return best;
        },
        [instances]
    );

    const moveActive = useCallback(
        (p: GoniometerPoint) => {
            if (!active) return;
            setInstances(prev =>
                prev.map((instance, i) => (i === active.instance ? moveHandle(instance, active.handle, p) : instance))
            );
        },
        [active]
    );

    const release = useCallback(() => setActive(null), []);

    // Keyboard alternative to dragging: clamped so a pivot at the frame edge drags its
    // arms by the applied (clamped) delta and the goniometer never tears apart.
    const nudge = useCallback((instanceIndex: number, handle: GoniometerHandleId, dx: number, dy: number) => {
        setInstances(prev =>
            prev.map((instance, i) => {
                if (i !== instanceIndex) return instance;
                if (handle === 'pivot') {
                    const nextX = clamp01(instance.pivot.x + dx);
                    const nextY = clamp01(instance.pivot.y + dy);
                    const appliedDx = nextX - instance.pivot.x;
                    const appliedDy = nextY - instance.pivot.y;
                    return {
                        pivot: { x: nextX, y: nextY },
                        arm1: { x: instance.arm1.x + appliedDx, y: instance.arm1.y + appliedDy },
                        arm2: { x: instance.arm2.x + appliedDx, y: instance.arm2.y + appliedDy },
                    };
                }
                return {
                    ...instance,
                    [handle]: { x: clamp01(instance[handle].x + dx), y: clamp01(instance[handle].y + dy) },
                } as GoniometerInstance;
            })
        );
    }, []);

    // Mirror every instance's arms around its own pivot's vertical axis
    const flipAll = useCallback(() => {
        setInstances(prev =>
            prev.map(instance => ({
                pivot: instance.pivot,
                arm1: { x: 2 * instance.pivot.x - instance.arm1.x, y: instance.arm1.y },
                arm2: { x: 2 * instance.pivot.x - instance.arm2.x, y: instance.arm2.y },
            }))
        );
    }, []);

    return { instances, active, reset, grabAt, moveActive, release, nudge, flipAll };
}
